<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <app-sub-header :title="title" :breadcrumbs="breadcrumbs" :back="false" :filter-by="filterBy" :filter-by-type="filterByType"></app-sub-header>
    </div>

    <div v-if="isReady" class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xl">

        <!-- Liste contacts -->
        <div class="card">
          <!-- Toolbar -->
          <div class="card-header align-items-center min-h-auto py-4">
            <!-- Compteur -->
            <span class="text-muted fs-7">
              {{ getScrollContacts.length }} / {{ getScrollContactsCount }} {{ $t('contacts.list') }}
            </span>

            <!-- Toggle vue liste / cards -->
            <div class="btn-group btn-group-sm ms-auto">
              <button @click="viewMode = 'list'" class="btn" :class="viewMode === 'list' ? 'btn-primary' : 'btn-light'">
                <i class="bi bi-list-ul"></i>
              </button>
              <button @click="viewMode = 'grid'" class="btn" :class="viewMode === 'grid' ? 'btn-primary' : 'btn-light'">
                <i class="bi bi-grid-3x3-gap"></i>
              </button>
            </div>
          </div>

          <div class="card-body p-0">

            <!-- Empty state -->
            <div v-if="!getScrollContacts.length" class="d-flex flex-column align-items-center justify-content-center py-20 text-center">
              <img src="/assets/media/illustrations/sketchy-1/illustration1-custom.svg?v=2" alt="" class="h-150px mb-6" />
              <h3 class="text-gray-700 fw-bold">{{ $t('contacts.no_contact_found') }}</h3>
              <p class="text-muted mb-5">{{ $t('button.clic') }}</p>
              <button v-if="$can('customer_create')" @click="openModalCreateContact" class="btn btn-primary btn-sm">
                {{ $t('contacts.new_contact') }}
              </button>
            </div>

            <!-- Vue cards horizontales -->
            <div v-else-if="viewMode === 'grid'" class="p-6">
              <div class="row g-3">
                <div class="col-12 col-md-4" v-for="contact in getScrollContacts" :key="contact.id + 'g'">
                  <router-link :to="`contact/${contact.id}/view`" class="contact-card text-decoration-none">
                    <!-- Liseré gauche selon type -->
                    <div class="contact-card-bar" :class="contact.contactType === 'society' ? 'bar-society' : 'bar-person'"></div>

                    <!-- Contenu horizontal -->
                    <div class="contact-card-content">
                      <!-- Avatar -->
                      <div class="contact-card-avatar-wrap">
                        <img v-if="contact.avatar" :src="contact.avatar_url" class="contact-card-avatar-img" />
                        <div v-else class="contact-card-avatar-initials">
                          {{ contact.lastname ? contact.lastname.charAt(0).toUpperCase() : '' }}{{ contact.firstname ? contact.firstname.charAt(0).toUpperCase() : '' }}
                        </div>
                      </div>

                      <!-- Infos -->
                      <div class="contact-card-info">
                        <div class="contact-card-name">{{ contact.fullname }}</div>
                        <div class="contact-card-meta" v-if="getFirstPhone(contact.phone)">
                          <i class="bi bi-telephone me-1"></i>{{ getFirstPhone(contact.phone) }}
                        </div>
                        <div class="contact-card-meta" v-if="contact.email">
                          <i class="bi bi-envelope me-1"></i>{{ formatEmail(contact.email) }}
                        </div>
                        <div class="d-flex gap-1 mt-1">
                          <span v-if="contact.customer" class="badge badge-light-success">{{ $t('contacts.customer') }}</span>
                          <span v-if="contact.supplier" class="badge badge-light-warning">{{ $t('contacts.supplier') }}</span>
                        </div>
                      </div>

                      <!-- Icône type -->
                      <div class="contact-card-type" :class="contact.contactType === 'society' ? 'type-society' : 'type-person'">
                        <i :class="contact.contactType === 'society' ? 'bi bi-building' : 'bi bi-person'"></i>
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div v-else id="contenu" class="contact-list-scroll">
              <table class="table align-middle table-row-hover table-row-dashed gy-4 gs-7 mb-0">
                <thead>
                  <tr class="text-start text-muted fw-bold fs-7 text-uppercase border-bottom border-gray-200">
                    <th class="ps-7" style="min-width:260px">{{ $t('contacts.fullname') }}</th>
                    <th style="min-width:120px">{{ $t('label.contactCategory') }}</th>
                    <th style="min-width:200px">{{ $t('contacts.email') }}</th>
                    <th style="min-width:160px">{{ $t('contacts.phone') }}</th>
                    <th style="min-width:160px">{{ $t('label.contactType') }}</th>
                    <th class="text-end pe-7" style="min-width:80px">{{ $t('contacts.actions') }}</th>
                  </tr>
                </thead>
                <tbody class="fw-semibold text-gray-600">
                  <tr v-for="contact in getScrollContacts" :key="contact.id">

                    <!-- Avatar + Nom -->
                    <td class="ps-7">
                      <div class="d-flex align-items-center gap-3">
                        <div class="contact-avatar" v-if="contact.avatar">
                          <img :src="contact.avatar_url" :alt="contact.lastname" />
                        </div>
                        <div class="contact-avatar contact-avatar-initials" v-else>
                          {{ contact.lastname ? contact.lastname.charAt(0).toUpperCase() : '' }}{{ contact.firstname ? contact.firstname.charAt(0).toUpperCase() : '' }}
                        </div>
                        <div>
                          <router-link :to="`contact/${contact.id}/view`" class="text-gray-800 text-hover-primary fw-bold fs-6 d-block">
                            {{ contact.fullname }}
                          </router-link>
                        </div>
                      </div>
                    </td>

                    <!-- Type particulier / société -->
                    <td>
                      <span v-if="contact.contactType === 'society'" class="badge badge-light-info">
                        <i class="bi bi-building me-1"></i>{{ $t('contacts.contact_types.society') }}
                      </span>
                      <span v-else class="badge badge-light-primary">
                        <i class="bi bi-person me-1"></i>{{ $t('particular') }}
                      </span>
                    </td>

                    <!-- Email -->
                    <td>
                      <a v-if="contact.email" :href="`mailto:${contact.email}`" class="text-gray-600 text-hover-primary">
                        {{ formatEmail(contact.email) }}
                      </a>
                      <span v-else class="text-muted">—</span>
                    </td>

                    <!-- Téléphone -->
                    <td>
                      <span v-if="getFirstPhone(contact.phone)" class="text-gray-700">
                        <i class="bi bi-telephone me-1 text-muted fs-8"></i>{{ getFirstPhone(contact.phone) }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>

                    <!-- Rôle (Client / Fournisseur) -->
                    <td>
                      <div class="d-flex gap-1 flex-wrap">
                        <span v-if="contact.customer" class="badge badge-light-success">{{ $t('contacts.customer') }}</span>
                        <span v-if="contact.supplier" class="badge badge-light-warning">{{ $t('contacts.supplier') }}</span>
                      </div>
                    </td>

                    <!-- Action -->
                    <td class="text-end pe-7">
                      <router-link :to="`contact/${contact.id}/view`" class="btn btn-sm btn-icon btn-light btn-active-light-primary">
                        <i class="bi bi-eye fs-6"></i>
                      </router-link>
                    </td>

                  </tr>
                </tbody>
              </table>


            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Loader initial -->
    <div v-else class="container-xxl">
      <Loader />
    </div>


    <!-- Modal créer contact -->
    <div class="modal fade" tabindex="-1" id="contactCreateModal">
      <div class="modal-dialog modal-xl">
        <create-contact id="1" parentId="contactCreateModal" :get-countries="getCountries" :contact="formCreateData" :modal="true"
          @success="loadFilter">
        </create-contact>
      </div>
    </div>

    <!-- Modal import/export -->
    <div class="modal fade" id="importContactModal" tabindex="-1" data-bs-backdrop="static">
      <div class="modal-dialog modal-md">
        <form @submit.prevent="importContactsData">
          <div class="modal-content">
            <div class="modal-header">
              <h3 class="modal-title">{{ $t('contacts.import_export') }}</h3>
              <div class="modal-icon-close-btn ms-2" data-bs-dismiss="modal" aria-label="Close">
                <span class="svg-icon svg-icon-2x">
                  <i class="fa fa-times"></i>
                </span>
              </div>
            </div>

            <div class="modal-body">
              <button v-if="$can('customer_export')" @click.prevent="exportContactFile" type="button" class="btn btn-bg-secondary w-100 mb-5" :disabled="isLoading">
                <span class="indicator-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width='20px' height='20px' viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1"
                        transform="rotate(90 12.75 4.25)" fill="currentColor"></rect>
                    <path
                        d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z"
                        fill="currentColor"></path>
                    <path
                        d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z"
                        fill="#C4C4C4"></path>
                  </svg>
                  {{ $t('contacts.export') }}
                </span>
                <span class="indicator-progress">
                  {{ $t('loading') }}
                  <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>

              <button v-if="$can('customer_import')" @click.prevent="downloadExcel" type="button" class="btn btn-bg-secondary w-100" :disabled="isLoading">
                <span class="indicator-label">
                  {{ $t('contacts.download_model') }}
                </span>
                <span class="indicator-progress">
                  {{ $t('loading') }}
                  <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>

              <div v-if="$can('customer_import')" class="fv-row fv-plugins-icon-container mb-5 mt-5">
                <label class="form-label"></label>
                <div class="d-flex justify-content-center align-items-center">
                  <input v-on:change="onContactFileChange" ref="contactFile" class="form-control form-control-solid border border-2" accept=".xlsx,.csv" type="file" id="contactFile">
                </div>
                <div class="fv-plugins-message-container invalid-feedback"></div>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-sm btn-light btn-light-close" data-bs-dismiss="modal">{{ $t('button.close') }}</button>
              <button v-if="$can('customer_import')" type="submit" class="btn btn-sm btn-primary fw-bolder" :disabled="isLoading">
                <span class="indicator-label">{{ $t('button.save') }}</span>
                <span class="indicator-progress">{{ $t('loading') }}
                  <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import AppSubHeader from "@/views/Contact/partials/AppSubHeaderIndex";
import { mapGetters, mapActions } from "vuex";
import CreateContact from "@/components/CreateContact";
import { BASE_IMAGE_URL } from "@/services/api";
import Loader from "@/components/Loader.vue";

export default {
  components: { CreateContact, AppSubHeader, Loader },
  data() {
    return {
      // mobile : cartes par défaut, le tableau (980 px) ne tient pas à l'écran
      viewMode: window.innerWidth < 768 ? 'grid' : 'list',
      title: 'breadcrumbs.contact.contacts',
      breadcrumbs: [
        { name: 'breadcrumbs.store.dashboard', path: '/admin/dashboard' },
        { name: 'breadcrumbs.contact.list', path: '/admin/dashboard' },
      ],
      formCreateData: {
        id: null,
        gender: null,
        contactType: 'particular',
        lastname: null,
        firstname: null,
        address: { street: null, street2: null, city: null, zipcode: null, country: null },
        customer: true,
        birthday: null,
        supplier: false,
        telephone: null,
        phoneCode: null,
        previewAvatar: BASE_IMAGE_URL + '/assets/blank.png',
        email: null,
        website: null
      },
      search: '',
      filter: 'all',
      contactType: 'all',
      filterBy: [
        { name: 'Tous', value: 'all', translate: 'label.allCategory' },
        { name: 'Societé', value: 'society', translate: 'society' },
        { name: 'Particulier', value: 'particular', translate: 'particular' },
      ],
      filterByType: [
        { name: 'Tous', value: 'all', translate: 'label.allType' },
        { name: 'Client', value: 'customer', translate: 'customer' },
        { name: 'Fournisseur', value: 'supplier', translate: 'supplier' },
        { name: 'Client & Fournisseur', value: 'customer_supplier', translate: 'customer_supplier' },
      ],
      isReady: false,
      formDataContactFileForm: { file: null },
      isLoadContact: false,
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters('countries', ['getCountries']),
    ...mapGetters('contacts', ['getScrollContacts', 'getScrollContactsCount']),
  },

  watch: {
    filter() {
      this.reload()
    },
    contactType() {
      this.reload()
    },
    search() {
      this.reload()
    },
  },

  async mounted() {
    await this.loadPermissions()
    this.isReady = false
    this.loadCountries()
    try {
      await this.chargeContacts()
    } finally {
      this.isReady = true
    }
  },

  methods: {
    ...mapActions('countries', ['loadCountries']),
    ...mapActions('contacts', ['loadScrollContacts', 'loadNextContacts', 'importContact', 'exportContact', 'downloadContactModel']),
    ...mapActions('permission', ['loadPermissions']),

    reload() {
      this.isReady = false
      this.isLoadContact = false
      this.loadScrollContacts({ search: this.search, filter: this.filter, type: this.contactType })
        .finally(() => { this.isReady = true })
    },

    getFirstPhone(phone) {
      try {
        const parsed = JSON.parse(phone)
        return Array.isArray(parsed) ? parsed[0]?.toString() : ''
      } catch {
        return phone?.toString() || ''
      }
    },

    formatEmail(email) {
      return email.length > 28 ? email.slice(0, 28) + '…' : email
    },

    openModalCreateContact() {
      $('#contactCreateModal').modal('toggle')
    },

    async exportContactFile() {
      try {
        this.isLoading = true
        await this.exportContact()
      } catch {}
      this.isLoading = false
    },

    onContactFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      this.formDataContactFileForm.file = files.length ? files[0] : null
    },

    async importContactsData() {
      if (!this.formDataContactFileForm.file) return
      this.isLoading = true
      try {
        const formData = new FormData()
        formData.append('file', this.formDataContactFileForm.file)
        await this.importContact(formData)
        await this.chargeContacts()
        $('#importContactModal').modal('toggle')
        this.formDataContactFileForm.file = null
        if (this.$refs.contactFile) this.$refs.contactFile.value = null
        window.toastr['success'](i18n.global.t('contact.import.success'))
      } catch {}
      this.isLoading = false
    },

    async downloadExcel() {
      const link = document.createElement('a')
      link.href = '/assets/media/excel/Fichier_modele_contact.xlsx'
      link.download = 'Fichier_modele_contact.xlsx'
      link.click()
    },

    async chargeContacts() {
      await this.loadScrollContacts({ search: this.search, filter: this.filter, type: this.contactType })
      await this.$nextTick()
      this.$contacts = document.querySelector('#contenu')
      if (this.getScrollContacts.length < this.getScrollContactsCount) {
        this.$contacts?.addEventListener('scroll', this.onScroll)
      }
    },

    async onScroll() {
      const el = this.$contacts
      if (!el) return
      if (el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight + 1 >= el.scrollHeight && !this.isLoadContact) {
        el.removeEventListener('scroll', this.onScroll)
        this.isLoadContact = true
        try {
          await this.loadNextContacts({ search: this.search, filter: this.filter })
        } catch {}
        this.isLoadContact = false
        if (this.getScrollContacts.length < this.getScrollContactsCount) {
          el.addEventListener('scroll', this.onScroll)
        }
      }
    },

    async loadFilter() {
      this.isReady = false
      await this.chargeContacts()
      this.isReady = true
    },
  }
}
</script>

<style scoped>
/* ── Vue cards contacts (horizontale) ── */
.contact-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.15s;
  height: 100%;
  text-decoration: none;
}
.contact-card:hover {
  box-shadow: 0 5px 18px rgba(0,0,0,0.12);
  transform: translateY(-2px);
}

/* Barre colorée gauche selon type */
.contact-card-bar {
  width: 4px;
  flex-shrink: 0;
  align-self: stretch;
}
.bar-society { background: #b8d9f5; }
.bar-person  { background: #cfc0eb; }

/* Ligne principale : avatar + infos + icône */
.contact-card-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  flex: 1;
  min-width: 0;
}

.contact-card-avatar-wrap {
  flex-shrink: 0;
}
.contact-card-avatar-img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
}
.contact-card-avatar-initials {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  /* dégradé à la couleur de la boutique (personnalisation), couleur par défaut sinon */
  background: linear-gradient(135deg, var(--kt-primary-active, #a87840), var(--kt-primary, #0b3d87));
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-card-info {
  flex: 1;
  min-width: 0;
}
.contact-card-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e2130;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.contact-card-meta {
  font-size: 0.75rem;
  color: #a1a5b7;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Icône type à droite */
.contact-card-type {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
}
.type-society { background: #eaf4fd; color: #6db8e8; }
.type-person  { background: #f0eaf9; color: #a98fd6; }

.contact-list-scroll {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}

.contact-list-scroll::-webkit-scrollbar {
  width: 5px;
}

.contact-list-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 8px;
}

.contact-list-scroll::-webkit-scrollbar-thumb {
  background: #b5b5c3;
  border-radius: 8px;
}

.contact-list-scroll::-webkit-scrollbar-thumb:hover {
  background: #7e8299;
}

.contact-list-scroll thead tr th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  box-shadow: 0 1px 0 #e4e6ef;
}

.contact-avatar {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;
  overflow: hidden;
}

.contact-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-avatar-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 158, 247, 0.1);
  color: #009ef7;
  font-weight: 700;
  font-size: 0.85rem;
}

</style>
